<template>
  <li
    v-if="rendered"
    v-show="visible"
    :class="{ 'v-select-option': true, selected, active, 'is-disabled': isDisabled }"
    :data-label="typeof currentLabel === 'object' ? undefined : currentLabel"
    :data-value="typeof value === 'object' ? undefined : value"
    @click="onClick()"
    @mouseover="onMouseover"
  >
    <div class="v-select-option__left">
      <VCheckbox v-if="select.multiple" class="v-select-option__checkbox" :model-value="selected" @click.stop @update:model-value="onClick()" />
      <slot>{{ currentLabel }}</slot>
    </div>

    <div v-if="$slots.right" class="v-select-option__right">
      <slot name="right" />
    </div>
  </li>
</template>

<script setup lang="ts">
import { debounce } from 'lodash-es'
import { computed, inject, onBeforeUnmount, onMounted, type Ref, ref, type UnwrapNestedRefs, watch } from 'vue'

import { delay } from '../../utils/helpers.ts'
import { $formKey, $optionGroupKey, $selectKey, injectRequired } from '../../utils/injections.ts'
import { useRenderQueue } from '../../utils/renderQueue.ts'
import VCheckbox from '../VCheckbox.vue'

export interface Props {
  value?: any
  label?: string | number
  disabled?: boolean
  isFilterOption?: boolean
  /**
   * The value to match against when filtering options.
   */
  filterValue?: string
}

export interface Option extends Props {
  id: number
  visible: Ref<boolean>
  selected: Ref<boolean>
  props: Props
  onClick: () => void
}

const { value = undefined, label = undefined, disabled = false, isFilterOption = false, filterValue = undefined } = defineProps<Props>()

defineSlots<{
  default?(): any
  right?(): any
}>()

const select = injectRequired($selectKey)
const group = inject($optionGroupKey, undefined)
const form = inject($formKey, undefined)
const rendered = ref(false)

const queue = useRenderQueue()
onMounted(() => queue.push(rendered))

const id = counter++

const currentLabel = computed(() => {
  return label ?? value
})

const active = computed(() => {
  return id === select.activeOption.value?.id
})

const isDisabled = computed(() => {
  return disabled || (!selected.value && select.multiple && (select.multipleLimit ?? 0) > 0 && select.model.value?.length === select.multipleLimit)
})

const visible = computed(() => {
  if (!select.filterable) {
    return true
  }

  const selectFilterValue = select.filterValue.value.toString().toLowerCase()
  const optionFilterValue = (filterValue ?? currentLabel.value)?.toString().toLowerCase()

  return optionFilterValue?.includes(selectFilterValue)
})

const selected = computed(() => {
  if (select.multiple && Array.isArray(select.model.value)) {
    return select.model.value.some((modelValue) => select.valueMatch(modelValue, value))
  }

  return select.valueMatch(select.model.value, value)
})

watch(
  () => select.popper.value?.visible,
  (value) => {
    if (value) {
      queue.delete(rendered)
      rendered.value = true
    }
  },
  {
    immediate: true,
  },
)

const option = {
  id,
  visible,
  selected,
  value,
  label,
  disabled,
  isFilterOption,
  filterValue,
  onClick,
} as unknown as UnwrapNestedRefs<Option> // when added to the reactive option objects, nested refs are unwrapped.

select.options[id] = option

if (group) {
  group.options[id] = option
}

onBeforeUnmount(() => {
  delete select.options[id]
  queue.delete(rendered)
  if (select.activeOption.value?.id === id) {
    select.activeOption.value = undefined
  }

  if (group) {
    delete group.options[id]
  }
})

function onClick() {
  if (isDisabled.value || !select) return

  if (select.ignoreClickOnSelectedOption && selected.value) {
    return
  }

  if (select.multiple) {
    const model = [...(select.model.value ?? [])]

    const index = model.findIndex((modelValue) => select.valueMatch(modelValue, value))
    if (index >= 0) {
      model.splice(index, 1)
    } else {
      model.push(value)
    }

    select.model.value = model
    updatePopper()

    if (select.prop) {
      form?.validateOneProp(select.prop)
    }

    if (select.noDropdown) {
      select.filterValue.value = ''
    }

    return
  }

  select.model.value = value

  select.popper.value?.hide()
  if (select.input.value) {
    select.input.value.focused = false
  }
  delay(1).then(() => {
    select.focus()

    if (select.prop) {
      form?.validateOneProp(select.prop)
    }
  })

  delay(300).then(() => {
    select.filterValue.value = ''
  })
}

function onMouseover() {
  if (select) {
    select.activeOption.value = option
  }
}

const updatePopper = debounce(function () {
  select.popper.value?.updatePopper()
}, 500)

defineExpose({ selected, option })
</script>

<script lang="ts">
let counter = 0
</script>

<style>
.v-select-option {
  line-height: 1;
  margin: 3px 0 3px 12px; /* 12px left to align with padding of scrollbar */
  padding: 10px;
  border-radius: 10px;
  margin-right: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  transition: background-color var(--transition);

  &:not(.is-disabled):hover,
  &:not(.is-disabled).active {
    background-color: var(--color-page-background);
    color: #000;
  }

  &.is-disabled {
    cursor: not-allowed;
    background-color: var(--color-page-background);
    opacity: 0.3;

    &.selected {
      background-color: rgba(var(--color-primary--rgb), 0.2);
    }
  }

  .v-select-option__left {
    flex-grow: 1;
    overflow: hidden;
    display: flex;
    align-items: center;
  }

  .v-select-option__right {
    font-size: 12px;
    color: var(--color-text-secondary);
    display: flex;
    align-items: center;
  }
}

.v-popper--select:not(.v-popper--select-multiple) .v-select-options__items .v-select-option {
  &.selected {
    background-color: var(--color-blue-1);
    font-weight: 500;

    &.active,
    &:not(.is-disabled):hover {
      background-color: var(--color-blue-2);
    }
  }
}

.v-select-option__checkbox {
  padding-left: 35px;
}
</style>
